import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../../services/api';

function BlobConfig({ tenantId }) {
  const [form, setForm] = useState({
    baseUrl: '',
    azureTenantId: '',
    clientId: '',
    clientSecretKey: '',
    storageAccountName: '',
    containerName: '',
    sasToken: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [hasExisting, setHasExisting] = useState(false);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/settings/blob/${tenantId}`);
      if (response.data.success && response.data.data) {
        const d = response.data.data;
        setForm({
          baseUrl: d.BaseUrl || '',
          azureTenantId: d.AzureTenantId || '',
          clientId: d.ClientId || '',
          clientSecretKey: d.ClientSecretKey || '',
          storageAccountName: d.StorageAccountName || '',
          containerName: d.ContainerName || '',
          sasToken: d.SASToken || '',
        });
        setHasExisting(true);
      } else {
        setForm({ baseUrl: '', azureTenantId: '', clientId: '', clientSecretKey: '', storageAccountName: '', containerName: '', sasToken: '' });
        setHasExisting(false);
      }
    } catch {
      toast.error('Failed to load blob configuration');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const handleTestConnection = async () => {
    setTesting(true);
    try {
      const response = await api.post('/settings/blob/test-connection', {
        baseUrl: form.baseUrl,
        azureTenantId: form.azureTenantId,
        clientId: form.clientId,
        clientSecretKey: form.clientSecretKey,
        storageAccountName: form.storageAccountName,
        containerName: form.containerName,
      });
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.warning(response.data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.baseUrl || !form.azureTenantId || !form.clientId || !form.clientSecretKey || !form.storageAccountName || !form.containerName) {
      toast.error('All fields marked with * are required');
      return;
    }
    setSaving(true);
    try {
      const response = await api.post('/settings/blob', { tenantId, ...form });
      if (response.data.success) {
        toast.success(response.data.message);
        setHasExisting(true);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: 40, color: '#999' }}>Loading...</div>;

  return (
    <div>
      <div className="form-row">
        <div className="form-group">
          <label className="required-label">Base URL</label>
          <input type="text" placeholder="https://account.blob.core.windows.net/" value={form.baseUrl} onChange={(e) => handleChange('baseUrl', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="required-label">Tenant Id</label>
          <input type="text" placeholder="Azure AD Tenant ID" value={form.azureTenantId} onChange={(e) => handleChange('azureTenantId', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="required-label">Client Id</label>
          <input type="text" placeholder="Application Client ID" value={form.clientId} onChange={(e) => handleChange('clientId', e.target.value)} />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label className="required-label">Client Secret Key</label>
          <input type="password" placeholder="Client Secret" value={form.clientSecretKey} onChange={(e) => handleChange('clientSecretKey', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="required-label">Storage Account Name</label>
          <input type="text" placeholder="Storage account name" value={form.storageAccountName} onChange={(e) => handleChange('storageAccountName', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="required-label">Container Name</label>
          <input type="text" placeholder="Container name" value={form.containerName} onChange={(e) => handleChange('containerName', e.target.value)} />
        </div>
      </div>

      <div className="settings-actions">
        <button className="btn btn-test" onClick={handleTestConnection} disabled={testing}>
          <span>☰</span> {testing ? 'Testing...' : 'Test Connection'}
        </button>
        <button className="btn btn-submit" onClick={handleSubmit} disabled={saving}>
          <span>💾</span> {saving ? 'Saving...' : 'Submit'}
        </button>
        <button className="btn btn-cancel" onClick={loadConfig}>
          <span>✕</span> Cancel
        </button>
      </div>
    </div>
  );
}

export default BlobConfig;
